import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputLabel,
  TextField,
  Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import userApi from '~/apis/user';
import { CloseIcon } from '~/assets/icons';
import BaseRow from '~/components/common/base-row/BaseRow';
import { cast, handleApiError } from '~/utils/helper';
import { IResetPassword, IUser } from '~/utils/interface/user';
import userValidation from '~/validation/user';
import BasicInfoUserDialog from '../basic-info/BasicInfoUserDialog';

type ResetPasswordDialogProps = {
  open: boolean;
  user?: IUser;
  setOpen?: (open: boolean, needRefreshData?: boolean) => void;
  setOpenBasicInfoUserDialog?: (open: boolean, needRefreshData?: boolean) => void;
};

const initialValues: IResetPassword = {
  new_password: '',
};

const ResetPasswordDialog = (props: ResetPasswordDialogProps) => {
  const { t } = useTranslation('admin/user');

  const { user } = props;

  const [openBasicInfoUserDialog, setOpenBasicInfoUserDialog] = useState<boolean>(false);
  const [newPassword, setNewPassword] = useState<string>('');

  const form = useFormik({
    validateOnChange: false,
    validationSchema: userValidation(t).resetPassword,
    initialValues,
    onSubmit: async (values) => {
      try {
        if (user?.username) {
          await userApi.resetPassword({ ...values, username: user?.username });
          setNewPassword(cast(values.new_password));
          handleSetOpen(false, false);
          setOpenBasicInfoUserDialog(true);
        }
      } catch (e: any) {
        handleApiError(e, true);
      }
    },
  });

  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    form.resetForm();
    if (props.setOpen) {
      props.setOpen(open, needRefreshData);
    }
  };

  return (
    <Box className='w-full'>
      <Dialog open={props.open} onClose={() => handleSetOpen(false)} fullWidth maxWidth='xs'>
        <DialogTitle className='bg-[#ED1002]'>
          {t('resetPassword.title')}
          <IconButton onClick={() => handleSetOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box component='form'>
            <BaseRow className='mb-[13px] gap-[15px] w-full !justify-between !items-center'>
              <Box className='font-bold'>{t('resetPassword.name')}</Box>
              <Typography>{user?.username}</Typography>
            </BaseRow>
            <BaseRow className='gap-[20px] mb-[20px] !items-center'>
              <Box className='w-1/2 font-bold flex items-center'>
                <InputLabel required={true}>{t('resetPassword.newPassword')}:</InputLabel>
              </Box>
              <TextField
                size='medium'
                className='w-1/2'
                type='password'
                name='new_password'
                placeholder={t('resetPassword.newPassword')}
                value={form.values?.new_password}
                onChange={form.handleChange}
                error={Boolean(form.errors?.new_password)}
                helperText={form.errors?.new_password}
              />
            </BaseRow>
            <BaseRow className='gap-[20px] mb-[20px] !items-center'>
              <Box className='w-1/2 font-bold flex items-center'>
                <InputLabel>{t('resetPassword.repeatNewPassword')}:</InputLabel>
              </Box>
              <TextField
                size='medium'
                className='w-1/2'
                type='password'
                name='new_password_confirmation'
                placeholder={t('resetPassword.repeatNewPassword')}
                onChange={form.handleChange}
                error={Boolean(form.errors?.new_password_confirmation)}
                helperText={form.errors?.new_password_confirmation}
              />
            </BaseRow>

            <Button
              variant='contained'
              color='secondary'
              className='min-w-[125px] flex ml-auto'
              onClick={() => form.handleSubmit()}
            >
              {t('confirm')}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <BasicInfoUserDialog
        open={openBasicInfoUserDialog}
        setOpen={setOpenBasicInfoUserDialog}
        data={{ user, password: cast(newPassword) }}
      />
    </Box>
  );
};

export default memo(ResetPasswordDialog);
